import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { pagePath } from '../lib/pages'
import { formatAgo, formatDateTime } from '../lib/format'
import { useAuth } from '../lib/auth'
import type { AccessSession, Customer } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'

interface Props {
  customerSlug: string | null
}

type Filter = 'laufend' | 'alle'

const KIND_LABEL: Record<AccessSession['kind'], string> = {
  ssh: 'SSH',
  gui: 'Web-GUI',
  portforward: 'Port-Tunnel',
}

/**
 * Was gerade offen ist und was zuletzt lief. Der Sinn der Seite ist die
 * Aufsicht: wer sitzt jetzt auf welchem Kundengerät, und wo ist die
 * Aufzeichnung dazu (docs/sicherheit.md).
 */
export function FernzugriffPage({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const { me, can } = useAuth()
  const [filter, setFilter] = useState<Filter>('alle')

  // Im Kundenkontext werden nur die Sitzungen dieses Kunden geholt; der
  // Server schränkt den Filter zusätzlich auf die eigenen Rechte ein.
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    enabled: Boolean(customerSlug),
  })
  const customer = customerSlug ? customers.data?.find((c) => c.slug === customerSlug) : undefined

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['access-sessions', 'page', customer?.id ?? null],
    queryFn: ({ signal }) =>
      apiGet<AccessSession[]>(customer ? `/access-sessions?kunde=${customer.id}` : '/access-sessions', signal),
    enabled: !customerSlug || Boolean(customer),
    refetchInterval: 20_000,
  })

  const end = useMutation({
    mutationFn: (id: string) => apiSend<void>('DELETE', `/access-sessions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['access-sessions'] }),
  })

  if (isError) return <ErrorBox error={error} what="Die Sitzungen" />
  if (isPending) return <Loading what="Sitzungen" />

  const running = data.filter((s) => !s.ended_at)
  const shown = filter === 'laufend' ? running : data

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Fernzugriff</h1>
          <div className="page-sub">
            {customerSlug ?? 'Alle Kunden'} · {running.length} laufend, {data.length} insgesamt
          </div>
        </div>
        <div className="row" style={{ gap: 6 }}>
          {(['alle', 'laufend'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className="btn btn-sm"
              aria-pressed={filter === f}
              style={
                filter === f
                  ? { background: 'var(--acc-soft)', color: 'var(--acc)', fontWeight: 600 }
                  : undefined
              }
              onClick={() => setFilter(f)}
            >
              {f === 'alle' ? `Alle (${data.length})` : `Laufend (${running.length})`}
            </button>
          ))}
        </div>
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">Sitzungen</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            Eine Sitzung endet spätestens nach 60 Minuten ohne Aktivität
          </span>
        </div>
        {shown.length === 0 ? (
          <EmptyState
            title="Keine Sitzungen"
            hint="Ein Zugriff beginnt auf der Seite des Geräts, über „SSH-Terminal“ oder „Web-GUI öffnen“."
          />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Wer</th>
                <th>Gerät</th>
                <th>Kunde / Standort</th>
                <th>Art</th>
                <th>Beginn</th>
                <th>Zustand</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((s) => (
                <tr key={s.id}>
                  <td>{s.user_name || s.user_email || 'unbekannt'}</td>
                  <td>
                    <Link
                      to="/k/$customerSlug/geraete/$deviceId"
                      params={{
                        customerSlug: s.customer_slug ?? customerSlug ?? '',
                        deviceId: s.device_id,
                      }}
                      className="mono"
                    >
                      {s.device_name ?? s.device_id}
                    </Link>
                  </td>
                  <td>
                    {s.customer_name ?? '–'}
                    {s.site_name ? ` · ${s.site_name}` : ''}
                  </td>
                  <td>
                    {KIND_LABEL[s.kind]}
                    {s.target_port ? <span className="muted mono"> (TCP {s.target_port})</span> : null}
                  </td>
                  <td title={formatDateTime(s.started_at)}>{formatAgo(s.started_at)}</td>
                  <td>
                    {s.ended_at ? (
                      <span className="badge badge-off">{s.end_reason || 'beendet'}</span>
                    ) : (
                      <span className="badge badge-ok">läuft</span>
                    )}
                  </td>
                  <td className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                    {s.recording_ref ? (
                      <a href={`/api/access-sessions/${s.id}/recording`}>Aufzeichnung</a>
                    ) : null}
                    {!s.ended_at && (s.user_id === me?.user_id || can('user.manage')) ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={end.isPending}
                        onClick={() => end.mutate(s.id)}
                      >
                        Beenden
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        Jeder Zugriff steht zusätzlich im Audit-Log. SSH-Sitzungen werden vollständig aufgezeichnet;
        bei der Web-GUI werden Beginn und Ende festgehalten.{' '}
        <Link to={pagePath('einstellungen', customerSlug)}>Einstellungen</Link>
      </p>
    </>
  )
}
